import { ScrollArea, Stack, Stepper, Table, Text, Title, useMantineTheme } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { enc, lib, SHA256 } from 'crypto-js'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ByteArray, bytesToHex, keccak256, toBytes, verifyMessage } from 'viem'
import { useAccount } from 'wagmi'

import RootButton from '@/components/Buttons/RootButton'
import UserDetails from '@/components/Details/UserDetails/UserDetails'
import ErrorMessage from '@/components/ErrorMessage'
import { ConfirmationLoading, ConfirmationReady } from '@/components/Identity/ConfirmModals/utils'
import classes from '@/components/Tables/WithdrawTable.module.css'
import { ELECTIONS_ADDRESS } from '@/constants/blockchain'
import { useElectionsContext } from '@/contexts/ElectionsContext'
import { useGovernanceContext } from '@/contexts/GovernanceContext'
import OMMAElection from '@/contracts/OMMAElection'
import { useChainFunctions } from '@/hooks/useChain'
import { useElectionResults } from '@/hooks/useElectionResults'
import { l1NativeWagmiConfig } from '@/plugins/auth/config'

interface VerifyModalProps {
  close: () => void
}

const STEPS = ['idle', 'signaturesAndProofs', 'submit']

interface Vote {
  candidate: string
  amount: bigint
}

const reduceBytes = (acc: Uint8Array, val: Uint8Array): Uint8Array => {
  const tempArray = new Uint8Array(acc.length + val.length)
  tempArray.set(acc)
  tempArray.set(val, acc.length)
  return tempArray
}

const hashVotes = (votesArray: Vote[]): string => {
  const bytesArray = votesArray
    .flatMap(({ candidate, amount }) => [toBytes(candidate), toBytes(amount)])
    .reduce(reduceBytes, new Uint8Array())

  return keccak256(bytesArray)
}

const bytesToWordArray = (data: ByteArray): lib.WordArray =>
  enc.Hex.parse(bytesToHex(data).slice(2))

const wordArrayToBytes = (data: lib.WordArray): ByteArray => toBytes(`0x${data.toString()}`)

const leafData = (address: string, candidate: string, amount: bigint, index: number): ByteArray => {
  const toHash = [
    toBytes(address),
    toBytes(candidate),
    toBytes(amount, { size: 32 }),
    toBytes(index, { size: 8 }),
  ].reduce(reduceBytes, new Uint8Array())
  const nonce = SHA256(bytesToWordArray(toHash))
  const data = [toBytes(amount, { size: 32 }), wordArrayToBytes(nonce)].reduce(
    reduceBytes,
    new Uint8Array()
  )
  return data
}

const verifyProof = (proof: string[], index: number, leaf: ByteArray, root: string): boolean => {
  let hash = SHA256(bytesToWordArray(leaf))
  hash = proof.reduce((currentHash, sibling) => {
    const combined =
      index % 2 === 0
        ? new Uint8Array([...wordArrayToBytes(currentHash), ...toBytes(sibling)])
        : new Uint8Array([...toBytes(sibling), ...wordArrayToBytes(currentHash)])
    index = Math.floor(index / 2)
    return SHA256(bytesToWordArray(combined))
  }, hash)

  return `0x${hash.toString()}` === root
}

const VerifyModal: React.FC<VerifyModalProps> = ({ close }) => {
  const { t } = useTranslation()
  const { address } = useAccount()
  const { writeContract } = useChainFunctions(l1NativeWagmiConfig)

  const { electionData } = useGovernanceContext()
  const { idx } = electionData
  const { userUnsplitVotes, userVotes, usedVoteBalance, refetchData } = useElectionsContext()
  const { userTotal, candidateResults } = useElectionResults(idx)

  const [step, setStep] = useState<string>(STEPS[0])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string>('')
  const [isReady, setIsReady] = useState<boolean>(false)
  const [active, setActive] = useState(0)
  const theme = useMantineTheme()
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`)

  const rows = useMemo(
    () =>
      userUnsplitVotes.map(row => (
        <Table.Tr key={row.timestamp + row.candidate}>
          <Table.Td className={classes.tableRow}>{row.timestamp}</Table.Td>
          <UserDetails
            userId={row.candidate}
            width={isMobile ? '130px' : '300px'}
            hidePfp={isMobile}
          />
          <Table.Td className={classes.tableRow}>{row.amount.display}</Table.Td>
        </Table.Tr>
      )),
    [userUnsplitVotes]
  )

  const verifySignatures = async () => {
    try {
      // Throw if necessary data is missing
      if (!address || !userVotes || !userTotal || candidateResults.length === 0) {
        throw new Error('Missing necessary data')
      }
      setIsLoading(true)
      setError('')
      // Check vote total
      let computedTotal = 0n
      userVotes.forEach(v => {
        v.votes.forEach(vt => {
          const data = candidateResults.find(result => result.candidate === vt.candidate)
          if (!data) {
            throw new Error('Candidate not found')
          }
          // Skip if candidate was removed
          if (data.status === 'REMOVED') {
            return
          }
          // Sum
          computedTotal += BigInt(vt.amount)
        })
      })
      if (computedTotal !== userTotal.value) {
        throw new Error('Vote total does not match')
      }

      // Check signatures on all user votes
      const validArray = await Promise.all(
        userVotes.map(a =>
          verifyMessage({
            address,
            message: `I want to cast votes with the following hash: ${hashVotes(
              a.votes.map(b => ({
                candidate: b.candidate,
                amount: BigInt(b.amount),
              }))
            )}`,
            signature: a.signature as `0x${string}`,
          })
        )
      )
      if (validArray.some(valid => !valid)) {
        throw new Error('Invalid signature for one or more votes')
      }
      // All good
      setIsLoading(false)
      setIsReady(true)
    } catch (err: any) {
      console.error(err)
      setIsLoading(false)
      setError(err.message || t('errors.signatureVerification', 'Failed to verify signatures'))
    }
  }

  const verifyProofs = async () => {
    try {
      // Throw if necessary data is missing
      if (!address || !userVotes || candidateResults.length === 0) {
        throw new Error('Missing necessary data')
      }
      setIsLoading(true)
      setError('')
      // Verify all merkle proofs for votes that are not removed
      const validArray = await Promise.all(
        userVotes
          .flatMap(vote => vote.votes)
          .map((vote, index) => {
            const data = candidateResults.find(result => result.candidate === vote.candidate)
            if (!data) {
              throw new Error('Candidate not found')
            }
            if (data.status === 'REMOVED') {
              return true
            }
            return verifyProof(
              vote.proof,
              vote.leafIndex,
              leafData(address, vote.candidate, BigInt(vote.amount), index),
              data.root
            )
          })
      )
      if (validArray.some(valid => !valid)) {
        throw new Error('Invalid proof for one or more votes')
      }
      // All good
      setIsLoading(false)
      setIsReady(true)
    } catch (err: any) {
      console.error(err)
      setIsLoading(false)
      setError(err.message || t('errors.proofVerification', 'Failed to verify merkle proofs'))
    }
  }

  const submitVerification = async () => {
    try {
      if (!idx) {
        throw new Error('Missing election index')
      }
      setIsLoading(true)
      setError('')
      await writeContract({
        address: ELECTIONS_ADDRESS,
        abi: OMMAElection,
        functionName: 'userVerifyVotes',
        args: [idx],
      })
      setIsLoading(false)
      setIsReady(true)
    } catch (err: any) {
      console.error(err)
      setIsLoading(false)
      setError(err.message || t('errors.submissionFailed', 'Failed to submit verification'))
    }
  }

  const nextStep = useCallback(async () => {
    switch (step) {
      case 'idle':
        setStep(STEPS[1])
        return
      case 'signaturesAndProofs':
        await verifySignatures()
        await verifyProofs()
        break
      case 'submit':
        await submitVerification()
        break
      default:
        console.error(`Unknown step: ${step}`)
    }
  }, [step, close])

  const stepBody = useMemo(() => {
    switch (step) {
      case 'idle':
        return (
          <Text>
            Verify your votes via merkle proof by clicking the Verify button below. This three-step
            process will verify your vote totals are correct, are associated with valid signatures,
            and went to the right candidates on the network. Verifying will also help increase other
            users’ confidence in the integrity of the election.
          </Text>
        )
      case 'signaturesAndProofs':
        return (
          <>
            <Title order={4}>Step 1: Vote Total, Signatures and Merkle Proofs</Title>
            <Text>
              The following table shows each time you voted, for a given candidate and amount. Click
              the Verify button to verify your vote total, signatures and merkle proofs of all your
              votes.
            </Text>

            <Table verticalSpacing={2} stickyHeader>
              <ScrollArea h={300}>
                <Table.Thead className={classes.tableHeader}>
                  <Table.Tr>
                    <Table.Th className={classes.tableHeader}>Timestamp</Table.Th>
                    <Table.Th className={classes.tableHeader}>Candidate</Table.Th>
                    <Table.Th className={classes.tableHeader}>Amount</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>{rows}</Table.Tbody>
              </ScrollArea>
              <Stack
                style={{
                  alignItems: 'flex-end',
                  padding: '0px 15px',
                }}
              >
                <Title order={5}>Total Votes: {usedVoteBalance.display}</Title>
              </Stack>
            </Table>
          </>
        )
      case 'submit':
        return (
          <>
            <Title order={4}>Step 2: Submit Verification</Title>
            <Text>
              Click the Submit button to finalize your verification. Completing this step will
              increase the Votes Verified meter and help increase other users’ confidence in the
              integrity of the election.
            </Text>
          </>
        )
      default:
        console.error(`Unknown step: ${step}`)
        return null
    }
  }, [step, rows])

  const stepButton = useMemo(() => {
    switch (step) {
      case 'submit':
        return t('buttons.submit', 'Submit')
      default:
        return t('buttons.verify', 'Verify')
    }
  }, [step])

  // Refresh data on mount and reset error on unmount
  useEffect(() => {
    refetchData()
    return () => {
      setError('')
    }
  }, [refetchData])

  return (
    <>
      {error && (
        <ErrorMessage
          message={t('components.verify.error', 'Verification Error')}
          details={error}
        />
      )}
      {isLoading && <ConfirmationLoading title={t('components.verify.loading', 'Verifying...')} />}
      {isReady && !isLoading && !error && (
        <ConfirmationReady
          message={[
            t('components.verify.total', 'Total Votes'),
            t('components.verify.signatures', 'Signatures'),
            t('components.verify.proofs', 'Merkle Proofs'),
          ]}
          tip="Verification Successful"
          success={() => {
            if (step === 'submit') {
              close()
            } else {
              setStep(STEPS[STEPS.indexOf(step) + 1])
              setActive(active + 1)
              setIsReady(false)
            }
          }}
          successMessage={
            step === 'submit' ? t('buttons.close', 'Close') : t('buttons.next', 'Next')
          }
        />
      )}
      {!isReady && !error && !isLoading && (
        <Stack>
          {step !== 'idle' && (
            <Stepper
              active={active}
              iconPosition="right"
              color={theme.colors.l1Primary[0]}
              iconSize={isMobile ? 27 : 32}
              styles={{
                step: {
                  padding: '5px',
                  flexFlow: 'column',
                },

                stepBody: {
                  padding: '10px',
                  margin: '0',
                },
                stepDescription: {
                  alignSelf: 'center',
                },
                stepLabel: {
                  alignSelf: 'center',
                  fontSize: isMobile ? '11px' : '14px',
                },
                stepIcon: {
                  borderWidth: 4,
                  color: theme.colors.title[0],
                },

                separator: {
                  marginLeft: -2,
                  marginRight: -2,
                  height: 5,
                },
                content: {
                  paddingTop: '5px',
                },
              }}
            >
              <Stepper.Step label="Step 1" description="Vote Total, Signatures and Merkle Proofs" />
              <Stepper.Step label="Step 2" description="Submit Verification" />
            </Stepper>
          )}
          {stepBody}
          <Stack style={{ flexDirection: 'row', marginTop: '20px' }}>
            <RootButton onClick={close} secondary style1={false} expand>
              {t('buttons.close', 'Close')}
            </RootButton>
            <RootButton
              onClick={nextStep}
              style1={false}
              expand
              disabled={
                isLoading || !address || !userVotes || !userTotal || candidateResults.length === 0
              }
            >
              {stepButton}
            </RootButton>
          </Stack>
        </Stack>
      )}
    </>
  )
}

export default VerifyModal
